import { useState } from "react";
import { Link } from "@/components/LangLink";
import { motion } from "framer-motion";
import { FileText, Ruler, ListChecks, CheckCircle2, Heart, Check, ArrowDown, ArrowRight, Play, MapPin } from "lucide-react";
import { PageHero, CTASection, Testimonials } from "@/components/sections";
import { Reveal } from "@/components/primitives";
import QuickBooking from "@/components/QuickBooking";
import { IMG } from "@/lib/site";

const FLOW = [
  { icon: FileText, title: "Prescrizione dello specialista", desc: "Il medico indica la classe di compressione più adatta alla condizione clinica." },
  { icon: Ruler, title: "Misurazione professionale", desc: "Rileviamo circonferenze e lunghezze con precisione, punto per punto." },
  { icon: ListChecks, title: "Scelta della taglia corretta", desc: "Individuiamo la misura esatta, non solo la classe di compressione." },
  { icon: CheckCircle2, title: "Prova della calza", desc: "Il paziente prova la calza: verifichiamo vestibilità e comfort reali." },
  { icon: Heart, title: "Maggiore comfort ed efficacia terapeutica", desc: "Un dispositivo su misura migliora l'aderenza e i risultati della terapia." },
];

const BENEFITS = [
  "Corretta vestibilità",
  "Maggiore efficacia della terapia",
  "Più comfort nella vita quotidiana",
  "Riduzione dei resi e delle sostituzioni",
  "Maggiore soddisfazione del paziente",
  "Fiducia nel trattamento e nel prodotto",
];

const VideoBlock = () => {
  const [playing, setPlaying] = useState(false);
  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="metodo-video-section">
      <div className="container-x">
        <div className="max-w-2xl mx-auto text-center">
          <span className="eyebrow justify-center">Guarda come funziona</span>
          <h2 className="mt-4 font-serif text-3xl lg:text-4xl font-bold text-navy">Il servizio di misurazione in sede.</h2>
          <p className="mt-4 text-muted-foreground">Un breve video mostra come si svolge la misurazione, passo dopo passo.</p>
        </div>
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-[1.75rem] border border-[#E7EDF2] shadow-[0_30px_70px_-30px_rgba(15,25,35,0.3)]" data-testid="metodo-video">
            {playing ? (
              <video
                controls
                autoPlay
                poster={IMG.measure}
                className="w-full aspect-video object-cover bg-ink"
              >
                <source src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4" type="video/mp4" />
              </video>
            ) : (
              <button
                onClick={() => setPlaying(true)}
                data-testid="metodo-video-play"
                className="group relative block w-full aspect-video"
              >
                <img src={IMG.measure} alt="Anteprima video misurazione" className="absolute inset-0 w-full h-full object-cover" />
                <span className="absolute inset-0 bg-ink/40 transition-colors duration-300 group-hover:bg-ink/30" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-20 w-20 items-center justify-center rounded-full bg-green text-white shadow-2xl transition-transform duration-300 group-hover:scale-110">
                    <Play size={30} className="ml-1 fill-white" />
                  </span>
                </span>
              </button>
            )}
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground italic">Video dimostrativo segnaposto — verrà sostituito con il video ufficiale Medisistem.</p>
        </div>
      </div>
    </section>
  );
};

const IlMetodo = () => (
  <>
    <PageHero
      eyebrow="Il Metodo Medisistem"
      title={"La misura giusta\nfa la differenza."}
      subtitle="La corretta misurazione è il primo passo verso l'efficacia della terapia. Non siamo un semplice distributore: siamo un Centro Specializzato nella Terapia Elasto-Compressiva."
      testid="metodo-hero"
    />

    {/* NARRATIVA */}
    <section className="py-20 lg:py-28 bg-white" data-testid="metodo-narrativa">
      <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <div className="overflow-hidden rounded-[1.75rem] border border-[#E7EDF2] shadow-[0_30px_70px_-30px_rgba(15,25,35,0.25)]">
            <img src={IMG.measure} alt="Servizio di misurazione professionale Medisistem" className="w-full h-[520px] object-cover" />
          </div>
        </div>
        <div>
          <span className="eyebrow">Il servizio di misurazione</span>
          <h2 className="mt-4 font-serif text-3xl lg:text-4xl font-bold text-navy leading-tight">
            Perché la misura conta più di quanto si creda.
          </h2>
          <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed" data-testid="metodo-narrativa-text">
            <p>Molti pazienti acquistano una calza elasto-compressiva senza che venga effettuata una corretta rilevazione delle misure. Spesso la prescrizione medica indica esclusivamente la classe di compressione, senza specificare la taglia, mentre il punto vendita non sempre dispone del tempo o delle competenze necessarie per eseguire una misurazione accurata.</p>
            <p>Una calza della misura sbagliata può risultare troppo stretta o troppo larga, compromettendo l’efficacia della terapia, riducendo il comfort e portando il paziente a ritenere erroneamente che il prodotto non sia valido. In molti casi questo genera sfiducia verso il trattamento o verso il marchio utilizzato, quando il vero problema è semplicemente una scelta non corretta della taglia.</p>
            <p>Per questo Medisistem mette a disposizione un <strong className="text-navy">servizio professionale di misurazione e prova della calza</strong>, eseguito da personale qualificato, presso la nostra sede oppure direttamente a domicilio. Il nostro obiettivo è garantire che ogni paziente indossi il dispositivo più adatto alle proprie caratteristiche, migliorando l’efficacia della terapia e il comfort quotidiano.</p>
            <p className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="metodo-domicilio-costo">
              <MapPin size={16} className="mt-0.5 shrink-0 text-amber-600" />
              Il servizio di misurazione a domicilio prevede un costo di uscita del tecnico, dovuto indipendentemente dall’eventuale acquisto successivo dei prodotti. L’importo viene comunicato al cliente prima della conferma dell’appuntamento.
            </p>
          </div>
        </div>
      </div>
    </section>

    <VideoBlock />

    {/* INFOGRAFICA + BENEFICI */}
    <section className="py-20 lg:py-28 bg-cloud" data-testid="metodo-infografica">
      <div className="container-x">
        <div className="max-w-2xl">
          <span className="eyebrow">Il metodo Medisistem</span>
          <h2 className="mt-4 font-serif text-3xl lg:text-4xl font-bold text-navy leading-tight">Un percorso semplice, in cinque passaggi.</h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-[1.15fr_0.85fr] gap-8 lg:gap-12 items-start">
          <div data-testid="metodo-flow">
            {FLOW.map((s, i) => (
              <div key={s.title}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center gap-5 rounded-2xl border border-[#E7EDF2] bg-white p-5 lg:p-6 shadow-sm"
                  data-testid={`metodo-flow-step-${i + 1}`}
                >
                  <span className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-green/10 text-green">
                    <s.icon size={24} />
                    <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-green text-white text-xs font-bold">{i + 1}</span>
                  </span>
                  <div>
                    <h3 className="font-serif text-lg lg:text-xl font-bold text-navy leading-snug">{s.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
                {i < FLOW.length - 1 && (
                  <div className="flex justify-center py-2 text-green/50">
                    <ArrowDown size={22} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="rounded-3xl bg-navy p-8 lg:p-10 text-white lg:sticky lg:top-28" data-testid="metodo-benefici">
            <span className="eyebrow text-green/90">Perché è importante?</span>
            <h3 className="mt-4 font-serif text-2xl lg:text-3xl font-bold">I vantaggi di una misura corretta.</h3>
            <ul className="mt-7 space-y-4">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green text-white">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  <span className="text-white/90">{b}</span>
                </li>
              ))}
            </ul>
            <Link to="/il-metodo#prenota" data-testid="metodo-benefici-cta" className="mt-8 btn-green w-full">
              Prenota una misurazione <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>

    <section className="py-20 lg:py-28 bg-cloud" data-testid="metodo-quickbooking">
      <div className="container-x">
        <QuickBooking />
      </div>
    </section>

    <Testimonials title="Chi ha provato la misurazione, lo racconta." intro="Le esperienze di pazienti e professionisti che hanno scelto il metodo Medisistem." />

    {/* QUOTE */}
    <section className="relative overflow-hidden py-20 lg:py-28" data-testid="metodo-quote">
      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,#1F5C48 0%,#175340 60%,#12362b 100%)" }} />
      <div className="absolute inset-0 grain-overlay" />
      <div className="container-x relative">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-serif text-6xl text-green leading-none">“</span>
          <blockquote className="mt-2 font-serif text-2xl sm:text-3xl lg:text-[2.25rem] leading-[1.3] font-medium text-white">
            La qualità di una calza elasto-compressiva non dipende solo dal prodotto, ma anche dalla corretta scelta della misura. Per questo Medisistem affianca ogni paziente con un servizio professionale di misurazione e prova, trasformando un semplice acquisto in un percorso di consulenza personalizzata.
          </blockquote>
          <p className="mt-8 text-sm uppercase tracking-[0.2em] text-white/60">Medisistem — Centro Specializzato</p>
        </div>
      </div>
    </section>

    <CTASection />
  </>
);

export default IlMetodo;
